import { useState, useEffect } from 'react';
import api from '../services/api';
import DataTable from '../components/DataTable';
import ContactForm from '../components/ContactForm';

export default function Contacts() {
  const [contacts, setContacts] = useState([]);
  const [filteredContacts, setFilteredContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [filters, setFilters] = useState({
    search: ''
  });

  useEffect(() => {
    fetchContacts();
  }, []);

  useEffect(() => {
    applyFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contacts, filters]);

  const fetchContacts = async () => {
    try {
      setLoading(true);
      const response = await api.get('/auth/contacts');
      setContacts(response.data.data);
    } catch (err) {
      setError('Failed to fetch contacts');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...contacts];

    if (filters.search) {
      const search = filters.search.toLowerCase();
      filtered = filtered.filter(c =>
        c.contactName?.toLowerCase().includes(search) ||
        c.contactEmail?.toLowerCase().includes(search) ||
        c.company?.toLowerCase().includes(search)
      );
    }

    // Sort by most recent first
    filtered.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

    setFilteredContacts(filtered);
  };

  const handleEdit = (contact) => {
    setEditingContact(contact);
    setShowForm(true);
  };

  const handleSave = async (data) => {
    await api.patch(`/auth/contacts/${editingContact._id}`, data);
    setShowForm(false);
    fetchContacts();
  };

  const columns = [
    { key: 'contactName', label: 'Name', sortable: true },
    { key: 'contactEmail', label: 'Email', sortable: true },
    { key: 'company', label: 'Company', render: (val) => val || 'N/A' },
    { key: 'phone', label: 'Phone', render: (val) => val || 'N/A' },
    {
      key: 'message',
      label: 'Message',
      render: (val) => val ? val.substring(0, 50) + (val.length > 50 ? '...' : '') : 'N/A'
    },
    {
      key: 'createdAt',
      label: 'Created',
      sortable: true,
      render: (val) => new Date(val).toLocaleString()
    }
  ];

  const renderFilters = () => (
    <div className="flex flex-col sm:flex-row gap-3">
      <input
        type="text"
        placeholder="Search by name, email, or company..."
        value={filters.search}
        onChange={(e) => setFilters({ ...filters, search: e.target.value })}
        className="flex-1 px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
      />
    </div>
  );

  if (loading) return (
    <div className="flex items-center justify-center min-h-screen">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
        <p className="mt-4 text-slate-600">Loading contacts...</p>
      </div>
    </div>
  );

  if (error) return (
    <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg">
      {error}
    </div>
  );

  return (
    <div>
      {showForm ? (
        <ContactForm
          contact={editingContact}
          onSave={handleSave}
          onCancel={() => setShowForm(false)}
        />
      ) : (
        <DataTable
          data={filteredContacts}
          columns={columns}
          onEdit={handleEdit}
          renderFilters={renderFilters}
        />
      )}
    </div>
  );
}
